import { useOrganization, useUser } from "@clerk/react";
import type { PostHog } from "posthog-js";
import { useEffect, useRef, useSyncExternalStore } from "react";

const key = import.meta.env.VITE_POSTHOG_KEY;

/** True when this build has a PostHog key, so there is something to ask about. */
export const analyticsAvailable = Boolean(key);

/**
 * Decision D46: nothing is recorded until the person says yes. Their answer is kept in this
 * browser; until then posthog-js isn't even downloaded, so it sets no cookies.
 */
export type RecordingChoice = "yes" | "no" | null;
const CHOICE_KEY = "backplan:recording";
const listeners = new Set<() => void>();

function readChoice(): RecordingChoice {
  try {
    const value = localStorage.getItem(CHOICE_KEY);
    return value === "yes" || value === "no" ? value : null;
  } catch {
    // Storage blocked: ask again, and record nothing meanwhile.
    return null;
  }
}

let current: RecordingChoice = readChoice();

/** Set once posthog-js has loaded and started. It's a separate chunk, fetched after a yes. */
let posthog: PostHog | undefined;
let loading: Promise<PostHog | undefined> | undefined;

function load(): Promise<PostHog | undefined> {
  if (!key) return Promise.resolve(undefined);
  loading ??= import("posthog-js").then(({ default: ph }) => {
    // Decision D45: every piece of page text is masked, in replays and in click events, because
    // the board shows the shop's customers and job references.
    ph.init(key, {
      api_host: import.meta.env.VITE_POSTHOG_HOST || "https://eu.i.posthog.com",
      defaults: "2026-01-30",
      person_profiles: "identified_only",
      mask_all_text: true,
      mask_all_element_attributes: true,
      session_recording: { maskAllInputs: true, maskTextSelector: "*" },
    });
    // Someone who said no earlier and yes now: clear PostHog's own stored opt-out.
    if (ph.has_opted_out_capturing()) ph.opt_in_capturing();
    posthog = ph;
    return ph;
  });
  return loading;
}

export function setRecordingChoice(choice: "yes" | "no"): void {
  try {
    localStorage.setItem(CHOICE_KEY, choice);
  } catch {
    // Not saved, so the question comes back next visit. The choice still applies now.
  }
  if (choice === "yes") {
    void load().then((ph) => {
      if (ph?.has_opted_out_capturing()) ph.opt_in_capturing();
      ph?.startSessionRecording();
    });
  } else if (posthog) {
    posthog.stopSessionRecording();
    posthog.opt_out_capturing();
  }
  current = choice;
  listeners.forEach((fn) => fn());
}

export function useRecordingChoice(): RecordingChoice {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => current,
  );
}

/** Starts PostHog on load only for someone who has already said yes in this browser. */
export function initAnalytics(): void {
  if (current === "yes") void load();
}

/** Ties events to the signed-in person and their shop, and forgets them on sign-out. */
export function AnalyticsIdentity(): null {
  const { user } = useUser();
  const { organization } = useOrganization();
  const choice = useRecordingChoice();
  const identified = useRef(false);
  const userId = user?.id;
  const email = user?.primaryEmailAddress?.emailAddress;
  const name = user?.fullName ?? undefined;
  const orgId = organization?.id;
  const orgName = organization?.name;

  useEffect(() => {
    if (choice !== "yes") return;
    void load().then((ph) => {
      if (!ph) return;
      if (userId && orgId) {
        ph.identify(userId, { email, name, organisation: orgName, organisationId: orgId });
        ph.register({ organisation: orgName });
        identified.current = true;
      } else if (!userId && identified.current) {
        ph.reset();
        identified.current = false;
      }
    });
  }, [choice, userId, email, name, orgId, orgName]);

  return null;
}

/** A link to the current session's replay, stored with feedback. Undefined unless recording is on. */
export function sessionReplayUrl(): string | undefined {
  return current === "yes" ? posthog?.get_session_replay_url({ withTimestamp: true }) : undefined;
}
